/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { InlineNotification } from 'carbon-components-react';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  selectAreSkillsReadOnly,
  selectAreEnvironmentsReadOnly,
  iaOngoingMaintenanceReadOnly
} from '../../store/access-control/selectors';
import messages from './messages';

export interface ReadOnlyNotificationProps {
  environmentId?: string;
  skillId?: string;
  readOnly?: boolean;
}

export const ReadOnlyNotification = ({
  readOnly,
  environmentId,
  skillId
}: ReadOnlyNotificationProps) => {
  const intl = useIntl();

  let isEnvironmentReadOnly, isSkillReadOnly;
  const environments = useSelector(selectAreEnvironmentsReadOnly);
  const skills = useSelector(selectAreSkillsReadOnly);

  // Only evaluate isEnvironmentReadOnly/isSkillReadOnly if readOnly isn't defined
  if (readOnly === undefined) {
    if (!environmentId && !skillId) {
      isEnvironmentReadOnly = environments.length > 0;
      isSkillReadOnly = skills.length > 0;
    } else {
      isEnvironmentReadOnly =
        environmentId &&
        environments.find(e => e.environment_id === environmentId);
      isSkillReadOnly = skillId && skills.find(s => s.skill_id === skillId);
    }
  }

  return (readOnly || isEnvironmentReadOnly || isSkillReadOnly) &&
    !iaOngoingMaintenanceReadOnly ? (
    <InlineNotification
      className="ReadOnlyNotification"
      kind="info"
      title={intl.formatMessage(messages.readOnly)}
      subtitle={intl.formatMessage(messages.readOnlySubtitle)}
      lowContrast
      hideCloseButton
    />
  ) : null;
};
